<template>
  <div class="custom-content-height">
    <div class="row justify-content-between my-0 mx-1 px-0 px-sm-4">
      <div
        :class="[
          'poppins text-dark-65 font-size-h1 display5-md font-weight-bolder col-12 col-md-4',
          { 'text-right': isRTL },
        ]"
      >
        {{ $t("my_analytics") }}
      </div>
      <div
        class="col-12 col-md-8 col-lg-5 d-flex justify-end align-center flex-wrap pa-2 px-0"
      >
        <FilterSelector
          :filters="filters"
          :update-data-of-charts="updateDataOfCharts"
        />
        <div
          class="col-12 col-md-5 col-lg-5 col-xl-3 ma-0 pa-5 pt-0 pt-md-5 px-2 d-flex justify-end align-center"
        >
          <date-range-picker
            class="w-100 mx-1"
            ref="picker"
            :opens="datePickerPosition"
            :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy' }"
            :close-on-esc="false"
            :showDropdowns="true"
            :ranges="ranges"
            min-date="01-01-2018"
            :max-date="new Date().toLocaleDateString()"
            control-container-class="form-control h-40px"
            v-model="dateRange"
          >
            <template v-slot:input="picker">
              <div class="h-100 d-flex align-center">
                <span class="svg-icon svg-icon-2 mr-2">
                  <v-icon size="22">mdi-calendar</v-icon>
                </span>
                <input
                  style="width: 100%"
                  type="text"
                  class="custom-input"
                  :placeholder="$t('select_a_date')"
                  :value="picker.rangeText === ' - ' ? '' : picker.rangeText"
                />
              </div>
            </template>

            <template v-slot:footer="picker">
              <div class="drp-buttons py-4 px-6 poppins">
                <button
                  type="button"
                  class="btn btn-light ls1 py-2 px-7"
                  @click="picker.clickCancel"
                >
                  {{ $t("cancel") }}
                </button>
                <button
                  type="button"
                  class="btn btn--export-filter ls1 py-2 px-4"
                  @click="picker.clickApply"
                >
                  {{ $t("apply") }}
                </button>
              </div>
            </template>
          </date-range-picker>
        </div>
      </div>
    </div>

    <div v-if="showData" class="min-h-500px">
      <div class="row mx-0 mx-sm-1 mt-md-4 px-2 px-sm-4">
        <analytics-orders
          v-if="userData.length > 0"
          :item="userData[0]"
          :filterData="dataOfFilters"
          :exportChart="loadDataFromServer"
        />
        <analytics-inventory
          v-if="userData.length > 1"
          :item="userData[1]"
          :filterData="dataOfFilters"
          :exportChart="loadDataFromServer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

import ApiService from "@/core/services/api.service";
import AnalyticsOrders from "@/own/components/dashboard/AnalyticsOrders";
import AnalyticsInventory from "@/own/components/dashboard/AnalyticsInventory";
import FilterSelector from "@/own/components/dashboard/filters/FilterSelector";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import { isRTLScreen } from "@/core/utils/general.util";

export default {
  name: "AnalyticsLayout",
  props: ["userData", "updateDashboardData", "filters"],
  components: {
    FilterSelector,
    AnalyticsOrders,
    AnalyticsInventory,
    DateRangePicker,
  },
  data() {
    return {
      dialog: false,
      dateTypeTrigger: "today",
      isVisible: true,
      dateType: "custom",
      dateRange: {
        startDate: null,
        endDate: null,
      },
      filterData: {},
      dateTypes: [
        { text: "Today", value: "today" },
        { text: "This week", value: "this_week" },
        { text: "Last week", value: "last_week" },
        { text: "Last 7 days", value: "last_7_days" },
        { text: "This month", value: "this_month" },
        { text: "Last month", value: "last_month" },
        { text: "Last 30 days", value: "last_30_days" },
        { text: "This year", value: "this_year" },
        { text: "Last year", value: "last_year" },
        { text: "All time", value: "all_time" },
        { text: "Custom", value: "custom" },
      ],
    };
  },
  created() {
    const today = this.getToday();
    this.dateRange.startDate = today;
    this.dateRange.endDate = today;
  },
  methods: {
    getToday() {
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    updateDataOfCharts(val) {
      const [key, value] = Object.entries(val)[0];
      this.filterData[key] = value;
      this.filterData = { ...this.filterData }; // force reactivity
      this.updateDashboardData({
        date_range_type: this.dateType,
        date_range: this.dateRange,
        ...this.filterData,
      });
    },
    updateDateOfCharts(val) {
      if (this.dateType !== val) {
        this.isVisible = false;
      }
      if (val === "custom") {
        if (!this.dialog) {
          this.dialog = true;
          this.isVisible = false;
          return;
        } else {
          this.dialog = false;
          this.dateType = this.dateTypeTrigger;
          this.updateDashboardData({
            date_range_type: val,
            date_range: this.dateRange,
            ...this.filterData,
          });
          this.isVisible = true;
          return;
        }
      } else {
        this.dateTypeTrigger = val;
        this.dateType = val;
        this.updateDashboardData({
          date_range_type: this.dateType,
          date_range: this.dateRange,
          ...this.filterData,
        });
        this.isVisible = true;
      }
    },
    loadDataFromServer(url) {
      this.$store.commit(SET_PAGE_LOADING, true);
      ApiService.post(url, this.dataOfFilters)
        .then((response) => {
          const link = document.createElement("a");
          link.href = response.data.url;
          link.download = "Export.csv";
          link.click();
          setTimeout(() => {
            this.$store.commit(SET_PAGE_LOADING, false);
          }, 1500);
        })
        .catch(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
  },
  computed: {
    isRTL() {
      return isRTLScreen(this.$i18n.locale);
    },
    dataOfFilters() {
      const today = this.getToday();
      const formattedStart = this.formatDate(this.dateRange.startDate);
      const formattedEnd = this.formatDate(this.dateRange.endDate);
      const dateRangeType =
        formattedStart === today && formattedEnd === today
          ? "today"
          : this.dateType;

      return {
        date_range_type: dateRangeType,
        date_range: [formattedStart, formattedEnd],
        ...this.filterData,
      };
    },
    showData() {
      return this.isVisible;
    },
    isDataReady() {
      return this.userData.length > 0;
    },
    datePickerPosition: function () {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return "left";
      }
      return "";
    },
    ranges: function () {
      let today = new Date();
      today.setHours(0, 0, 0, 0);

      let yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);

      return {
        [this.$t("today")]: [today, today],
        [this.$t("yesterday")]: [yesterday, yesterday],
        [this.$t("last_7_days")]: [
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("last_30_days")]: [
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("this_month")]: [
          new Date(today.getFullYear(), today.getMonth(), 1),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("last_month")]: [
          new Date(today.getFullYear(), today.getMonth() - 1, 1),
          new Date(today.getFullYear(), today.getMonth(), 0),
        ],
        [this.$t("this_year")]: [
          new Date(today.getFullYear(), 0, 1),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("last_year")]: [
          new Date(today.getFullYear() - 1, 0, 1),
          new Date(today.getFullYear() - 1, 11, 31),
        ],
      };
    },
  },
};
</script>

<style scoped>
.rounded-md {
  border-radius: 0.6rem;
}

.shadow-sm {
  box-shadow: rgb(76 78 100 / 12%) 0px 2px 8px 0px !important;
}

.bg-light-primary {
  background-color: rgba(115, 34, 176, 0.1);
}

.bg-light-success {
  background-color: rgba(115, 34, 176, 0.15);
}

.bg-light-info {
  background-color: rgba(195, 44, 128, 0.1);
}

.bg-light-warning {
  background-color: rgba(195, 44, 128, 0.15);
}

.text-dark-50 {
  color: rgba(0, 0, 0, 0.5);
}

.text-dark-75 {
  color: rgba(0, 0, 0, 0.75);
}

.max-h-45px {
  max-height: 45px;
}
</style>
